import {
  Component,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  Inject,
  Renderer2,
  ElementRef,
} from '@angular/core';
import {
  PieceHtmlContainerGeneralSettings,
} from 'extensions/oy/piece-html-container/front/models/piece-html-container-settings';
import {
  IdtComponent,
} from 'shared/models/idt-components/idt-component.models';
import {
  forkJoin,
  Observable,
  Subject,
} from 'rxjs';
import {
  DomSanitizer,
  SafeHtml,
  SafeStyle,
} from '@angular/platform-browser';
import { CmcToCmsParserService } from '../services/cmc-to-cms-parser.service';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ActivatedRoute } from '@angular/router';
import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';


declare global {
  interface Window {
    jQuery: any;
    $: any;
    inditex?: any;
  }
}

@Component({
  selector: 'piece-html-container-render',
  templateUrl: './piece-html-container-render.component.html',
  styleUrls: ['./piece-html-container-render.component.scss'],
})
export class PieceHtmlContainerRenderComponent
  extends UnsubscribeComponent
  implements OnDestroy {
  // Component inputs
  private _piece?: IdtComponent;
  @Input() public set piece(pieceReceived: IdtComponent | undefined) {
    if (pieceReceived) {
      this._piece = pieceReceived;
      this.doRenderCycle();
    }
  }

  public get piece(): IdtComponent | undefined {
    return this._piece;
  }

  // Component settings and settings parsed
  public htmlContent: string = '';
  public cssStyles: string = '';
  public jsCode: string = '';
  public htmlContentSafe?: SafeHtml;
  public cssStylesSafe?: SafeStyle;

  public idtScript: any;
  public jsScript: any;
  public jqueryScript: any;

  // Other component variables
  private elementQuery!: string;
  private host!: HTMLElement;
  private customQueryFunction!: string;
  private readonly JQUERY_CDN_ROUTE: string = 'https://code.jquery.com/jquery-2.2.4.min.js';

  constructor(
    private cd: ChangeDetectorRef,
    private cmsParseService: CmcToCmsParserService,
    @Inject('Window') protected window: Window,
    @Inject('Document') protected document: Document,
    private renderer: Renderer2,
    private domSanitizer: DomSanitizer,
    private renderSlugService: RenderSlugService,
    private activatedRoute: ActivatedRoute,
    private elementRef: ElementRef,
    private renderEventsService: RenderEventsService
  ) {
    super();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.deleteScripts();
  }

  private deleteScripts() {
    if (this.idtScript) {
      this.renderer.removeChild(this.document.body, this.idtScript);
    }
    if (this.jsScript) {
      this.renderer.removeChild(this.document.body, this.jsScript);
    }
    if (this.jqueryScript) {
      this.renderer.removeChild(this.document.body, this.jqueryScript);
    }
  }

  // executes when the piece data is set
  private doRenderCycle() {
    this.recoverGeneralSettings();
    forkJoin([
      this.parseHtml(),
      this.parseStyles(),
    ]).pipe(
      withLatestFrom(this.cmsParseService.getContext()),
      takeUntil(this.clearSubscriptions$)
    ).subscribe(([parsedData, context]) => {
      this.host = this.elementRef.nativeElement.getRootNode().host;
      if (isDefined(this.host)) {
        if (!this.host.getAttribute('data-wcid')) {
          let slugId: string = `${this.renderSlugService.getSlug()}${(Math.random() + 1).toString(36).substring(7)}`;
          // Slugs can contain dashses, this will brake JS execution when defining the function
          slugId = slugId.replace(/-/g, '');
          this.host.setAttribute('data-wcid', slugId);
        }
        this.elementQuery = `[data-wcid="${this.host.getAttribute('data-wcid')}"]`;
        this.customQueryFunction = `queryOnRenderIdt${this.host.getAttribute('data-wcid')}`;
      }

      const [html, css] = parsedData;
      this.htmlContentSafe = html;
      this.cssStylesSafe = css;
      // Detect changes to "paint" new html and css before executing JS
      this.cd.detectChanges();

      this.parseJs(context);
    });
  }

  private addAnchorEventListeners() {
    const shadowRoot = this.document.querySelector(this.elementQuery)?.shadowRoot || this.document;
    shadowRoot.querySelectorAll("a[href^='#']").forEach((element) => {
      const href: string = element.getAttribute('href') || '';
      element.removeAttribute('href');
      element.addEventListener('click', (event) => {
        const anchorEl = shadowRoot.getElementById(href?.replace('#', ''));
        anchorEl?.scrollIntoView({ behavior: 'smooth' });
      });
    })
  }

  private recoverGeneralSettings() {
    if (this.piece?.settings?.general) {
      const generalSettings: PieceHtmlContainerGeneralSettings = this.piece.settings.general;

      // Recover and bypass settings
      this.htmlContent = generalSettings?.htmlContent ? generalSettings?.htmlContent : '';
      this.cssStyles = generalSettings?.cssStyles ? generalSettings?.cssStyles : '';
      this.jsCode = generalSettings?.jsCode ? generalSettings?.jsCode : '';
    }
  }

  private parseStyles(): Observable<SafeStyle> {
    // Remove style and script tags before parse
    this.cssStyles = this.cssStyles.trim().replace(/<\/?style[^>]*>/g, '').replace(/<script[^]*script>/g, '');
    return this.cmsParseService.parse(this.cssStyles)
      .pipe(
        takeUntil(this.clearSubscriptions$),
        map((data) => {
          data = '<style>' + data + '</style>';
          return this.domSanitizer.bypassSecurityTrustHtml(data);
        }),
      );
  }

  private parseHtml(): Observable<SafeHtml> {
    return this.cmsParseService.parse(this.htmlContent).pipe(
      takeUntil(this.clearSubscriptions$),
      map((data) => {
        return this.domSanitizer.bypassSecurityTrustHtml(data);
      }));
  }

  private parseJs(context: EcommerceContext) {
    // Remove style and script tags before parse
    this.jsCode = this.jsCode.trim().replace(/<\/?script[^>]*>/g, '');
    this.cmsParseService.parse(this.jsCode)
      .subscribe(jsCode => {
        const shadowRoot = this.document.querySelector(this.elementQuery)?.shadowRoot;
        const jsCodeSafe = this.overrideQuerysOnShadowRoot(jsCode, shadowRoot);

        // Include jQuery and append all scripts
        if (typeof this.window.jQuery !== 'undefined') {
          this.loadAndExecuteJavascript(shadowRoot, this.elementQuery, context, jsCodeSafe);
        } else {
          const jquery = this.createJqueryFromCdn();
          jquery.onload = () => this.loadAndExecuteJavascript(shadowRoot, this.elementQuery, context, jsCodeSafe);
          this.renderer.appendChild(this.document.body, jquery);
        }

        this.cd.detectChanges();
        this.addAnchorEventListeners();
        // Only add event listener inside shadow root (webcomponent).
        if (shadowRoot) {
          this.addNavigationEventListeners();
        }
      });
  }

  private overrideQuerysOnShadowRoot(jsCodeSafe: string, shadowRoot?: ShadowRoot | null) {
    if (isDefined(shadowRoot)) {
      jsCodeSafe = jsCodeSafe.replace(/document.querySelector\(/g, `document.querySelector('${this.elementQuery}').shadowRoot.querySelector(`);
      jsCodeSafe = jsCodeSafe.replace(/document.querySelectorAll\(/g, `document.querySelector('${this.elementQuery}').shadowRoot.querySelectorAll(`);
      jsCodeSafe = jsCodeSafe.replace(/document.getElementById\(/g, `document.querySelector('${this.elementQuery}').shadowRoot.getElementById(`);
      jsCodeSafe = jsCodeSafe.replace(/\$\(/g, `${this.customQueryFunction}(`);
      jsCodeSafe = jsCodeSafe.replace(/jQuery\(/g, `${this.customQueryFunction}(`);
    }
    return jsCodeSafe;
  }

  private createJqueryFromCdn(): HTMLScriptElement {
    const jquery = this.document.createElement('script');
    // We get jquery from cdn to avoid increasing the size of the webcomponent
    jquery.src = this.JQUERY_CDN_ROUTE;
    jquery.type = 'text/javascript';

    return jquery;
  }

  private loadAndExecuteJavascript(
    shadowRoot: ShadowRoot | null | undefined,
    componentQuery: string,
    context: EcommerceContext,
    jsCodeSafe: string,
  ) {
    this.jqueryScript = this.getJqueryScriptToOverride(shadowRoot, componentQuery);

    this.idtScript = this.createIdtScript(context);
    this.jsScript = this.generateSluggedScript(jsCodeSafe, true, 'jsScript');
  }

  private getJqueryScriptToOverride(shadowRoot: ShadowRoot | null | undefined, componentQuery?: string) {
    if (!isDefined(shadowRoot) || !isDefined(componentQuery)) { return; }

    const text = `
      if (window.jQuery) {
        var ${this.customQueryFunction} = function(query) {
          if (typeof query !== 'string') { return window.jQuery(query); }
          var element = document.querySelector('${componentQuery}') && document.querySelector('${componentQuery}').shadowRoot.querySelectorAll(query);
          // If element has no items, search outside the shadowRoot
          if (element && element.length === 0) {
            element = document.querySelectorAll(query);
          }
          element = element && element.length === 1 ? element[0] : element;
          return window.jQuery(element);
        }
      }
    `;

    return this.generateSluggedScript(text, true, 'jqueryScript');
  }

  private createIdtScript(context: EcommerceContext) {
    if (this.window.inditex) {
      // Inditex object is defined, will use it.
      return this.overrideLocaleInfo();
    }

    const iStoreJson = JSON.stringify(context?.store);
    const iRestVersion = context.config.apiBaseUrl.split('/').pop() || 2;
    const iCategoryId = this.getCategoryId();
    const inditex = `
      var inditex = inditex || {};
      inditex.onReady = function(f) {setTimeout(f, 0);};
      inditex.iStoreJSON=${iStoreJson};
      inditex.iStoreId=${context.store.id};
      inditex.iAppId=${context.config.appId};
      inditex.iCatalogId=${context.currentCatalogId};
      inditex.iLangId=${context.currentLanguage.id};
      inditex.isRTL=${context.currentLanguage.direction === 'rtl'};
      inditex.iCategoryId=${iCategoryId};
      inditex.iRestVersion=${iRestVersion};
    `;

    return this.generateSluggedScript(inditex, true, 'idtScript');
  }

  private getCategoryId() {
    if (this.activatedRoute.snapshot.params.categoryId) {
      return this.activatedRoute.snapshot.params.categoryId;
    }

    let categoryIdFromWindow = '0';
    const categoryUrlRegex = this.window.location.href?.match(/c([0-9]+)/);
    if (categoryUrlRegex?.length) {
      categoryIdFromWindow = categoryUrlRegex[1];
    }
    return categoryIdFromWindow;
  }

  private overrideLocaleInfo() {
    if (this.window.inditex.iStoreJSON?.details?.currencyInfo &&
      !this.window.inditex.iStoreJSON?.details?.locale) {
      // Spots uses locale property instead currencyInfo
      this.window.inditex.iStoreJSON.details.locale = this.window.inditex.iStoreJSON.details.currencyInfo;
    }
  }

  private generateSluggedScript(content: string, shouldAddToBody: boolean, id: string) {
    const jsScript = this.document.createElement('script');
    jsScript.textContent = content;
    jsScript.setAttribute('id', id);
    jsScript.setAttribute('data-slug', this.host?.getAttribute('data-wcid') || '');
    jsScript.setAttribute('type', 'text/javascript');

    if (shouldAddToBody) {
      this.renderer.appendChild(this.document.body, jsScript);
    }

    return jsScript;
  }

  /**
   * Creates a generic event listener that listens to navigations and emits the corresponding render idt event.
   */
  private addNavigationEventListeners() {
    this.elementRef.nativeElement.addEventListener('click', (event: PointerEvent) => {
      const isTargetAnchor = event.target instanceof HTMLAnchorElement && isDefined(event.target.href);
      const anchorInPath = isDefined(event.composedPath) ?
        event.composedPath().find(target => target instanceof HTMLAnchorElement) : undefined;
      const anchorElement = isTargetAnchor ? event.target : anchorInPath;
      // Only emit navigation events on clicks on "a" that is not an anchor to page section.
      if (
        isDefined(anchorElement)
        && anchorElement instanceof HTMLAnchorElement
        && anchorElement.href !== ''
        && anchorElement.href[0] !== '#'
      ) {
        event.preventDefault();
        const url = anchorElement.href;
        // Check data-absolute attribute to know if link is absolute or should navigate using SPA.
        const isAbsolute = anchorElement.getAttribute('data-absolute') !== null;
        // Check target attribute to know if link should be opened in a new page.
        const shouldOpenInNewTab = anchorElement.getAttribute('target') === '_blank';
        // Look for category and product ids in url.
        const categoryId = url.match(/c([0-9]+)/);
        const productId = url.match(/p([0-9]+)/);
        // Get link type
        let type = 'external';
        if (categoryId) { type = 'category'; }
        if (productId) { type = 'product'; }
        const linkData = {
          event,
          extra: {
            type,
            categoryId: categoryId ? categoryId[0].substring(1): undefined,
            productId: productId ? productId[0].substring(1) : undefined,
            relativeLink: !isAbsolute,
            newWindow: shouldOpenInNewTab,
            src: url
          },
          location: this.piece?.name
        } as RenderEvent;
        // Emit render idt event.
        this.renderEventsService.events.next(linkData);
      }
    });
  }
}
